/**
 * AI thread summaries ("TL;DR" under a thread's root; docs/features/thread-summaries.md). Pure rules shared by the
 * server (when a run starts, what it hashes, how the model's answer is read) and the clients (the card's state).
 */
import type { Locale, ThreadSummary } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

/** A thread is summarized automatically once it has this many replies… */
export const AUTO_FIRST_AT = 5;
/** …and again after every this many more. */
export const AUTO_EVERY = 5;
/** Most bullets kept from the model's answer. */
export const MAX_BULLETS = 4;
/** A run older than this is considered dead (its lock is ignored). */
export const RUN_STALE_MS = 10 * 60 * 1000;
/** A Summarize the client asked for that hasn't produced a summary by then shows the error state. */
export const REQUEST_TIMEOUT_MS = 90 * 1000;

/**
 * Should a new reply start an automatic run? At AUTO_FIRST_AT replies, then every AUTO_EVERY more.
 * `lastAutoAt` is the reply count at the last automatic start (null = never).
 */
export function autoSummaryDue(replyCount: number, lastAutoAt: number | null | undefined): boolean {
  if (replyCount < AUTO_FIRST_AT) return false;
  if (lastAutoAt === null || lastAutoAt === undefined) return true;
  return replyCount - lastAutoAt >= AUTO_EVERY;
}

/** Is a run that started at `runningSince` still in flight? */
export function runActive(runningSince: number | null | undefined, now: number): boolean {
  return runningSince !== null && runningSince !== undefined && now - runningSince < RUN_STALE_MS;
}

/**
 * Fingerprint of what a summary covers: the thread's message ids with their edit times, in order (FNV-1a 32, hex).
 * Equal hashes ⇒ nothing to re-summarize.
 */
export function threadHash(items: { id: string; editedAt?: number | null }[]): string {
  let h = 0x811c9dc5;
  const text = items.map((m) => `${m.id}:${m.editedAt ?? 0}`).join("|");
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

const PT_WORDS = new Set([
  "de", "que", "não", "nao", "para", "com", "uma", "um", "os", "as", "do", "da", "dos", "das", "em", "no", "na", "é",
  "está", "esta", "vou", "vamos", "mas", "isso", "também", "tambem", "pra", "por", "você", "voce", "eu", "ele", "ela",
  "já", "ja", "ainda", "aqui", "quando", "porque", "então", "entao", "sim", "obrigado", "valeu", "fazer", "precisa",
]);
const EN_WORDS = new Set([
  "the", "and", "to", "of", "is", "it", "that", "for", "you", "with", "on", "this", "we", "are", "be", "will", "can",
  "not", "but", "have", "what", "so", "if", "just", "i'll", "let's", "should", "need", "thanks", "yes", "do", "does",
  "my", "our", "they", "there", "at", "by", "from", "was", "would", "could", "also", "about",
]);

/** The thread's language from its text (Portuguese vs English stop words); ties and silence fall back. */
export function threadLanguage(texts: string[], fallback: Locale): Locale {
  let pt = 0;
  let en = 0;
  for (const t of texts) {
    for (const w of t.toLowerCase().split(/[^\p{L}']+/u)) {
      if (w === "") continue;
      if (PT_WORDS.has(w)) pt++;
      else if (EN_WORDS.has(w)) en++;
    }
  }
  if (pt === en) return fallback;
  return pt > en ? "pt-BR" : "en";
}

/**
 * The bullets in the model's answer: list lines (`-`, `*`, `•`, `1.`) without their markers; when there are none,
 * the non-empty lines. Headings (`TL;DR`, `#…`, `Summary:`) are dropped; at most `max`.
 */
export function parseBullets(text: string, max: number = MAX_BULLETS): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== "");
  const marker = /^(?:[-*•]|\d+[.)])\s+/;
  const listed = lines.filter((l) => marker.test(l)).map((l) => l.replace(marker, "").trim());
  const heading = /^(?:#+\s|tl;?dr\b|summary\b|resumo\b)/i;
  const out = (listed.length > 0 ? listed : lines.filter((l) => !heading.test(l)))
    .map((l) => l.replace(/^\*\*(.+)\*\*$/, "$1").trim())
    .filter((l) => l !== "");
  return out.slice(0, max);
}

/** "Summary · updated …": how long ago it was written. */
export function updatedLabel(generatedAt: number, now: number): MessageDescriptor {
  const minutes = Math.floor(Math.max(0, now - generatedAt) / 60_000);
  if (minutes < 1) return msg("summary.updated.justNow");
  if (minutes < 60) return msg("summary.updated.minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return msg("summary.updated.hours", { count: hours });
  return msg("summary.updated.days", { count: Math.floor(hours / 24) });
}

export type SummaryCardInput = {
  /** The server can write summaries (bootstrap `aiAvailable`). */
  available: boolean;
  /** The workspace allows them (`workspace.aiThreadSummaries !== false`). */
  enabled: boolean;
  /** The thread's replies now. */
  replyCount: number;
  summary?: ThreadSummary | undefined;
  /** When I last asked for one (Summarize / Refresh), if that is still unanswered. */
  requestedAt?: number | undefined;
  /** My last request failed (refused or timed out). */
  failed?: boolean | undefined;
  collapsed: boolean;
  now: number;
};

export type SummaryCard =
  | { kind: "hidden" }
  /** No summary yet: a "Summarize" action. */
  | { kind: "empty"; loading: boolean; failed: boolean }
  | {
      kind: "ready";
      bullets: string[];
      updated: MessageDescriptor;
      /** Replies arrived after it was written: Refresh is highlighted. */
      stale: boolean;
      loading: boolean;
      failed: boolean;
      collapsed: boolean;
    };

/** What the thread pane shows under the root. Hidden when the server or the workspace has summaries off, or no replies. */
export function summaryCard(input: SummaryCardInput): SummaryCard {
  if (!input.available || !input.enabled || input.replyCount < 1) return { kind: "hidden" };
  const s = input.summary;
  const loading = input.requestedAt !== undefined && input.now - input.requestedAt < REQUEST_TIMEOUT_MS && (s === undefined || s.generatedAt < input.requestedAt);
  const failed = input.failed === true && !loading;
  if (s === undefined) return { kind: "empty", loading, failed };
  return {
    kind: "ready",
    bullets: s.bullets,
    updated: updatedLabel(s.generatedAt, input.now),
    stale: input.replyCount > s.replyCount,
    loading,
    failed,
    collapsed: input.collapsed,
  };
}

/** Storage key for the card's collapsed state (per workspace, remembered across threads). */
export function collapsedKey(wsId: string): string {
  return `threadSummary.collapsed.${wsId}`;
}
